import { useState, useCallback } from 'react';
import { authApi } from '@/services/api';

export interface TotpSetupData {
  secret: string;
  otpAuthUri: string;
}

export function useTotp() {
  const [setupData, setSetupData] = useState<TotpSetupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [savedBackupCodes, setSavedBackupCodes] = useState<Array<{ code: string; used: boolean }>>([]);

  const startSetup = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getTotpSetup();
      if (response?.success && response.data) {
        setSetupData(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to start 2FA setup'));
    } finally {
      setLoading(false);
    }
  }, []);

  const verifySetup = useCallback(async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.verifyTotpSetup(token);
      if (response?.success && response.data) {
        setBackupCodes(response.data.backupCodes);
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to verify TOTP token'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const disable2FA = useCallback(async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.disableTotp(token);
      return response?.success ?? false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to disable 2FA'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBackupCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getBackupCodes();
      if (response?.success && response.data) {
        setSavedBackupCodes(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch backup codes'));
    } finally {
      setLoading(false);
    }
  }, []);

  const generateNewBackupCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.generateBackupCodes();
      if (response?.success && response.data) {
        setBackupCodes(response.data);
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to generate backup codes'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    setupData,
    backupCodes,
    savedBackupCodes,
    loading,
    error,
    startSetup,
    verifySetup,
    disable2FA,
    fetchBackupCodes,
    generateNewBackupCodes,
  };
}
